import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import './Admin.css';

interface Ticket {
    id: string;
    ticketNumber: string;
    ticketType: string;
    eventType: string;
    amount: number;
    buyerName: string;
    buyerEmail: string;
    buyerPhone: string;
    eventDate: string | null;
    createdAt: string;
    description?: string;
    venue?: string;
}

const PER_PAGE_OPTIONS = [10, 20, 40, 50, 60, 80, 100, 200, 500, 1000];

export function Admin() {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [ticketType, setTicketType] = useState('normal');
    const [eventType, setEventType] = useState('musical');
    const [count, setCount] = useState<number | ''>('');
    const [amount, setAmount] = useState<number | ''>('');
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [description, setDescription] = useState('');
    const [venue, setVenue] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [activeTab, setActiveTab] = useState<'tickets' | 'buyers'>('tickets');
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [downloading, setDownloading] = useState<Record<string, boolean>>({});
    const navigate = useNavigate();

    // Pagination state — separate for each tab
    const [ticketsPage, setTicketsPage] = useState(1);
    const [buyersPage, setBuyersPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Edit modal state
    const [editTicket, setEditTicket] = useState<Ticket | null>(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editAmount, setEditAmount] = useState<number | ''>('');
    const [editTicketType, setEditTicketType] = useState('');
    const [editEventType, setEditEventType] = useState('');
    const [editEventDate, setEditEventDate] = useState('');
    const [editEventTime, setEditEventTime] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editVenue, setEditVenue] = useState('');

    const showTemporaryMessage = (msg: string) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const fetchTickets = async () => {
        setFetchLoading(true);
        try {
            const resp = await fetch(`${config.API_BASE_URL}/tickets`);
            const data = await resp.json();
            // Sort latest first
            const sorted = [...data].sort((a: Ticket, b: Ticket) =>
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            setTickets(sorted);
        } catch (err) {
            console.error(err);
        } finally {
            setFetchLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('admin_token')) {
            navigate('/login');
        } else {
            fetchTickets();
        }
    }, [navigate]);

    // Keep page in valid bounds when data or perPage changes
    useEffect(() => {
        if (activeTab === 'tickets') {
            const maxPage = Math.max(1, Math.ceil(tickets.length / perPage));
            if (ticketsPage > maxPage) {
                setTicketsPage(maxPage);
            }
        } else {
            const buyersCount = tickets.filter(t => t.buyerName && t.buyerName !== 'Admin Bulk').length;
            const maxPage = Math.max(1, Math.ceil(buyersCount / perPage));
            if (buyersPage > maxPage) {
                setBuyersPage(maxPage);
            }
        }
    }, [tickets, perPage, activeTab, ticketsPage, buyersPage]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (count === '' || amount === '') {
            showTemporaryMessage('Please fill in both Count and Amount');
            return;
        }
        if (eventDate && !eventTime) {
            showTemporaryMessage('Please provide a time for the event date');
            return;
        }
        if (!eventDate && eventTime) {
            showTemporaryMessage('Please provide a date for the event time');
            return;
        }
        setLoading(true);
        setMessage('');

        try {
            const resp = await fetch(`${config.API_BASE_URL}/tickets/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ticketType,
                    eventType,
                    count: Number(count),
                    amount: Number(amount),
                    buyerName: 'Admin Bulk',
                    buyerEmail: 'admin@gmail.com',
                    ...(eventDate ? { eventDate: new Date(`${eventDate}T${eventTime}:00`).toISOString() } : {}),
                    ...(description.trim() ? { description: description.trim() } : {}),
                    ...(venue.trim() ? { venue: venue.trim() } : {})
                })
            });

            if (resp.ok) {
                showTemporaryMessage(`Successfully created ${count} tickets`);
                setCount('');
                setAmount('');
                setEventDate('');
                setEventTime('');
                setDescription('');
                setVenue('');
                setTicketsPage(1);
                setIsCreateModalOpen(false);
                fetchTickets();
            } else {
                showTemporaryMessage('Error creating tickets');
            }
        } catch (err) {
            showTemporaryMessage('Server error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string, ticketNumber: string) => {
        if (!window.confirm(`Are you sure you want to delete ticket ${ticketNumber}?`)) return;
        try {
            const resp = await fetch(`${config.API_BASE_URL}/tickets/${id}`, {
                method: 'DELETE'
            });
            if (resp.ok) {
                showTemporaryMessage(`Deleted ticket ${ticketNumber} successfully.`);
                fetchTickets();
            } else {
                showTemporaryMessage('Error deleting ticket');
            }
        } catch (err) {
            showTemporaryMessage('Server error while deleting');
        }
    };

    const handleDeleteAll = async () => {
        if (tickets.length === 0) return;
        if (!window.confirm('Are you sure you want to delete ALL tickets? This action cannot be undone.')) return;
        try {
            const resp = await fetch(`${config.API_BASE_URL}/tickets`, {
                method: 'DELETE'
            });
            if (resp.ok) {
                showTemporaryMessage('Deleted all tickets successfully.');
                fetchTickets();
            } else {
                showTemporaryMessage('Error deleting tickets');
            }
        } catch (err) {
            showTemporaryMessage('Server error while deleting all');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        navigate('/');
    };

    const handleDownload = async (ticketId: string, ticketNumber: string) => {
        setDownloading(prev => ({ ...prev, [ticketId]: true }));
        try {
            const res = await fetch(`${config.API_BASE_URL}/tickets/${ticketId}/download`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ticket-${ticketNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Download failed. Please try again.');
        } finally {
            setDownloading(prev => ({ ...prev, [ticketId]: false }));
        }
    };

    // Open edit modal
    const openEdit = (t: Ticket) => {
        setEditTicket(t);
        setEditAmount(t.amount);
        setEditTicketType(t.ticketType);
        setEditEventType(t.eventType);
        if (t.eventDate) {
            const d = new Date(t.eventDate);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            setEditEventDate(`${yyyy}-${mm}-${dd}`);
            setEditEventTime(`${hh}:${min}`);
        } else {
            setEditEventDate('');
            setEditEventTime('');
        }
        setEditDescription(t.description || '');
        setEditVenue(t.venue || '');
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editTicket) return;
        if (editEventDate && !editEventTime) {
            showTemporaryMessage('Please provide a time for the event date');
            return;
        }
        if (!editEventDate && editEventTime) {
            showTemporaryMessage('Please provide a date for the event time');
            return;
        }
        setEditLoading(true);
        try {
            const resp = await fetch(`${config.API_BASE_URL}/tickets/${editTicket.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ticketType: editTicketType,
                    eventType: editEventType,
                    amount: Number(editAmount),
                    ...(editEventDate ? { eventDate: new Date(`${editEventDate}T${editEventTime}:00`).toISOString() } : { eventDate: null }),
                    description: editDescription.trim() || null,
                    venue: editVenue.trim() || null,
                })
            });
            if (resp.ok) {
                showTemporaryMessage(`Ticket ${editTicket.ticketNumber} updated successfully.`);
                setEditTicket(null);
                fetchTickets();
            } else {
                showTemporaryMessage('Error updating ticket');
            }
        } catch (err) {
            showTemporaryMessage('Server error while updating');
        } finally {
            setEditLoading(false);
        }
    };

    const purchasedCount = tickets.filter(t => t.buyerName && t.buyerName !== 'Admin Bulk').length;
    const unpurchasedCount = tickets.filter(t => !t.buyerName || t.buyerName === 'Admin Bulk').length;

    const allDisplayedTickets = tickets.filter(t => {
        if (activeTab === 'buyers') {
            return t.buyerName && t.buyerName !== 'Admin Bulk';
        }
        return true;
    });

    // Pagination logic
    const currentPage = activeTab === 'tickets' ? ticketsPage : buyersPage;
    const setCurrentPage = activeTab === 'tickets' ? setTicketsPage : setBuyersPage;
    const totalPages = Math.ceil(allDisplayedTickets.length / perPage);
    const paginatedTickets = allDisplayedTickets.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage
    );

    const goToPage = (p: number) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    // Generate page numbers to show (max 5 visible)
    const getPageNumbers = () => {
        const pages: (number | '...')[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="admin-container">
            <div className="admin-header">
                <h1>Ticketing Platform</h1>
                <p>Manage Event Ticketing</p>
                <button onClick={handleLogout} className="admin-btn" style={{ background: 'linear-gradient(90deg, #ff5050, #ff0055)', marginTop: '20px' }}>Logout</button>
            </div>

            {message && <div className="admin-message">{message}</div>}

            <div className="admin-tabs">
                <button
                    type="button"
                    className={`admin-tab-btn ${activeTab === 'tickets' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('tickets'); setTicketsPage(1); }}
                >
                    <span className="tab-icon">🎟️</span> Ticket Inventory
                </button>
                <button
                    type="button"
                    className={`admin-tab-btn ${activeTab === 'buyers' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('buyers'); setBuyersPage(1); }}
                >
                    <span className="tab-icon">👤</span> Buyer Details
                </button>
            </div>

            <div className="admin-content full-width">
                <div className="admin-panel tickets-list-panel">
                    <div className="panel-header">
                        <h2>{activeTab === 'tickets' ? 'Generated Tickets' : 'Buyer Transaction Records'}</h2>
                        <div className="panel-actions">
                            {activeTab === 'tickets' && (
                                <button
                                    onClick={() => {
                                        setTicketType('normal');
                                        setEventType('musical');
                                        setCount('');
                                        setAmount('');
                                        setEventDate('');
                                        setEventTime('');
                                        setVenue('');
                                        setDescription('');
                                        setMessage('');
                                        setIsCreateModalOpen(true);
                                    }}
                                    className="refresh-btn launch-trigger-btn"
                                    style={{ marginRight: '10px', color: '#00ffcc', borderColor: 'rgba(0, 255, 204, 0.3)' }}
                                >
                                    Create Tickets
                                </button>
                            )}
                            {activeTab === 'buyers' && (
                                <div className="stats-container">
                                    <div className="stats-card stats-purchased">
                                        <span className="stats-dot dot-purchased"></span>
                                        <span className="stats-label">Purchased:</span>
                                        <span className="stats-value">{purchasedCount}</span>
                                    </div>
                                    <div className="stats-card stats-unpurchased">
                                        <span className="stats-dot dot-unpurchased"></span>
                                        <span className="stats-label">Unpurchased:</span>
                                        <span className="stats-value">{unpurchasedCount}</span>
                                    </div>
                                </div>
                            )}
                            <button onClick={handleDeleteAll} className="refresh-btn" style={{ marginRight: '10px', color: '#ff5050', borderColor: 'rgba(255, 80, 80, 0.3)' }}>Delete All</button>
                            <button onClick={fetchTickets} className="refresh-btn">Refresh</button>
                        </div>
                    </div>



                    {fetchLoading ? (
                        <div className="loading">Loading database insight...</div>
                    ) : (
                        <div className="table-area-wrap">
                            {/* Scrollable table */}
                            <div className="table-container">
                                <table className="tickets-table">
                                    <thead>
                                        {activeTab === 'tickets' ? (
                                            <tr>
                                                <th className="t-count">#</th>
                                                <th>Number</th>
                                                <th>Class</th>
                                                <th>Event</th>
                                                <th>Venue</th>
                                                <th>Description</th>
                                                <th>Value</th>
                                                <th>Event Date</th>
                                                <th>Created</th>
                                                <th>Action</th>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <th className="t-count">#</th>
                                                <th>Number</th>
                                                <th>Class</th>
                                                <th>Event</th>
                                                <th>Venue</th>
                                                <th>Buyer Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Value</th>
                                                <th>Event Date</th>
                                                <th>Purchased On</th>
                                                <th>Action</th>
                                            </tr>
                                        )}
                                    </thead>
                                    <tbody>
                                        {paginatedTickets.map((t, idx) => (
                                            <tr key={t.id}>
                                                <td className="t-count t-mono">{(currentPage - 1) * perPage + idx + 1}</td>
                                                <td className="t-mono">{t.ticketNumber}</td>
                                                <td>
                                                    <span className={`badge badge-${t.ticketType.toLowerCase()}`}>
                                                        {t.ticketType.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>{t.eventType.toUpperCase()}</td>

                                                {/* Venue column */}
                                                <td className="t-venue">
                                                    {t.venue ? (
                                                        <span className="venue-badge">
                                                            <span className="venue-badge-icon">📍</span>
                                                            <span className="venue-badge-text">{t.venue}</span>
                                                        </span>
                                                    ) : (
                                                        <span className="t-no-date">—</span>
                                                    )}
                                                </td>

                                                {activeTab === 'tickets' && (
                                                    <td className="t-description">
                                                        {t.description
                                                            ? <span className="t-desc-text" title={t.description}>{t.description}</span>
                                                            : <span className="t-no-date">—</span>
                                                        }
                                                    </td>
                                                )}

                                                {activeTab === 'buyers' && (
                                                    <>
                                                        <td className="t-small t-buyer-name">{t.buyerName || '—'}</td>
                                                        <td className="t-small">{t.buyerEmail || '—'}</td>
                                                        <td className="t-small">{t.buyerPhone || '—'}</td>
                                                    </>
                                                )}

                                                <td>₹{Number(t.amount).toFixed(2)}</td>
                                                <td className="t-event-date">
                                                    {t.eventDate ? (
                                                        <span className="event-date-badge">
                                                            <span className="event-date-icon">📅</span>
                                                            <span>
                                                                <span className="event-date-d">{new Date(t.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                                                <span className="event-date-t">{new Date(t.eventDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="t-no-date">—</span>
                                                    )}
                                                </td>
                                                <td className="t-small">{new Date(t.createdAt).toLocaleString()}</td>
                                                <td>
                                                    {activeTab === 'buyers' ? (
                                                        <div className="actions-cell">
                                                            <button
                                                                onClick={() => setSelectedTicket(t)}
                                                                className="action-btn view-btn"
                                                                title="View Ticket"
                                                            >
                                                                👁️
                                                            </button>
                                                            <button
                                                                onClick={() => handleDownload(t.id, t.ticketNumber)}
                                                                disabled={downloading[t.id]}
                                                                className="action-btn download-btn"
                                                                title="Download Ticket"
                                                            >
                                                                {downloading[t.id] ? '⏳' : '📥'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(t.id, t.ticketNumber)}
                                                                className="action-btn delete-btn-circle"
                                                                title="Delete Ticket"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="actions-cell">
                                                            <button
                                                                onClick={() => openEdit(t)}
                                                                className="action-btn edit-btn"
                                                                title="Edit Ticket"
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(t.id, t.ticketNumber)}
                                                                className="action-btn delete-btn-circle"
                                                                title="Delete Ticket"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {paginatedTickets.length === 0 && (
                                            <tr>
                                                <td colSpan={activeTab === 'tickets' ? 10 : 12} className="t-empty">
                                                    No tickets established in this timeline.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>{/* end table-container */}

                            {/* Pagination Controls — outside scroll area, always visible */}
                            {allDisplayedTickets.length > 0 && (
                                <div className="pagination-controls">
                                    <div className="pagination-info">
                                        Showing <strong>{(currentPage - 1) * perPage + 1}</strong>–<strong>{Math.min(currentPage * perPage, allDisplayedTickets.length)}</strong> of <strong>{allDisplayedTickets.length}</strong> tickets
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="pagination-pages">
                                            <button
                                                className="page-btn page-nav"
                                                onClick={() => goToPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                title="Previous page"
                                            >
                                                ‹
                                            </button>

                                            {getPageNumbers().map((p, idx) =>
                                                p === '...' ? (
                                                    <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        className={`page-btn ${currentPage === p ? 'active' : ''}`}
                                                        onClick={() => goToPage(p as number)}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            )}

                                            <button
                                                className="page-btn page-nav"
                                                onClick={() => goToPage(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                title="Next page"
                                            >
                                                ›
                                            </button>
                                        </div>
                                    )}

                                    <div className="per-page-wrap">
                                        <label className="per-page-label">Rows</label>
                                        <select
                                            className="per-page-select"
                                            value={perPage}
                                            onChange={e => {
                                                setPerPage(Number(e.target.value));
                                                setTicketsPage(1);
                                                setBuyersPage(1);
                                            }}
                                        >
                                            {PER_PAGE_OPTIONS.map(n => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                            <option value={100000}>All</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* PDF Ticket Preview Modal */}
            {selectedTicket && (
                <div className="ticket-modal-overlay" onClick={() => setSelectedTicket(null)}>
                    <div className="ticket-modal-content pdf-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-top-bar">
                            <a
                                href={`${config.API_BASE_URL}/tickets/${selectedTicket.id}/download`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="open-tab-btn"
                            >
                                ↗️ Open in New Tab
                            </a>
                            <button className="close-modal-btn-static" onClick={() => setSelectedTicket(null)}>✕ Close</button>
                        </div>

                        {/* Ticket card with location */}
                        <div className="modal-ticket-card">
                            <div className="modal-ticket-stripe" style={{
                                background: selectedTicket.ticketType === 'premium'
                                    ? 'linear-gradient(90deg,#9b59b6,#d288ec)'
                                    : selectedTicket.ticketType === 'basic'
                                        ? 'linear-gradient(90deg,#3498db,#74b9ff)'
                                        : 'linear-gradient(90deg,#00b894,#55efc4)'
                            }} />
                            <div className="modal-ticket-body">
                                <div className="modal-ticket-top">
                                    <div>
                                        <span className={`modal-ticket-badge badge-${selectedTicket.ticketType}`}>
                                            {selectedTicket.ticketType.toUpperCase()}
                                        </span>
                                        <div className="modal-ticket-event">{selectedTicket.eventType.toUpperCase()} EVENT</div>
                                    </div>
                                    <div className="modal-ticket-num">{selectedTicket.ticketNumber}</div>
                                </div>

                                {/* Venue row */}
                                {selectedTicket.venue && (
                                    <div className="modal-ticket-venue">
                                        <span className="modal-venue-icon">📍</span>
                                        <span className="modal-venue-text">{selectedTicket.venue}</span>
                                    </div>
                                )}

                                <div className="modal-ticket-divider">
                                    <div className="modal-dot modal-dot-l" />
                                    <div className="modal-dashes" />
                                    <div className="modal-dot modal-dot-r" />
                                </div>

                                <div className="modal-ticket-details">
                                    <div className="modal-detail">
                                        <span className="modal-detail-label">Amount</span>
                                        <span className="modal-detail-val">₹{Number(selectedTicket.amount).toFixed(2)}</span>
                                    </div>
                                    {selectedTicket.eventDate && (
                                        <div className="modal-detail">
                                            <span className="modal-detail-label">Date</span>
                                            <span className="modal-detail-val">
                                                {new Date(selectedTicket.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </span>
                                        </div>
                                    )}
                                    {selectedTicket.eventDate && (
                                        <div className="modal-detail">
                                            <span className="modal-detail-label">Time</span>
                                            <span className="modal-detail-val">
                                                {new Date(selectedTicket.eventDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    )}
                                    <div className="modal-detail">
                                        <span className="modal-detail-label">Buyer</span>
                                        <span className="modal-detail-val">{selectedTicket.buyerName || '—'}</span>
                                    </div>
                                </div>

                                {selectedTicket.description && (
                                    <div className="modal-ticket-desc">{selectedTicket.description}</div>
                                )}
                            </div>
                        </div>

                        <div className="modal-pdf-container">
                            <iframe
                                src={`${config.API_BASE_URL}/tickets/${selectedTicket.id}/download`}
                                title={`Ticket ${selectedTicket.ticketNumber} PDF Preview`}
                                className="pdf-iframe-preview"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Ticket Modal */}
            {editTicket && (
                <div className="ticket-modal-overlay" onClick={() => setEditTicket(null)}>
                    <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="edit-modal-header">
                            <div>
                                <h3 className="edit-modal-title">Edit Ticket</h3>
                                <div className="edit-modal-num">#{editTicket.ticketNumber} <span className="edit-readonly-badge">read-only</span></div>
                            </div>
                            <button className="close-modal-btn-static" onClick={() => setEditTicket(null)}>✕</button>
                        </div>

                        <form onSubmit={handleEditSubmit} className="edit-modal-form">
                            <div className="form-group row">
                                <div className="col">
                                    <label>Ticket Type</label>
                                    <select value={editTicketType} onChange={e => setEditTicketType(e.target.value)}>
                                        <option value="premium">Premium</option>
                                        <option value="basic">Basic</option>
                                        <option value="normal">Normal</option>
                                    </select>
                                </div>
                                <div className="col">
                                    <label>Event Type</label>
                                    <select value={editEventType} onChange={e => setEditEventType(e.target.value)}>
                                        <option value="musical">Musical</option>
                                        <option value="techno">Techno</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Amount (₹)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    required
                                    value={editAmount}
                                    onChange={e => setEditAmount(e.target.value === '' ? '' : Number(e.target.value))}
                                />
                            </div>

                            <div className="form-group row">
                                <div className="col">
                                    <label>Event Date</label>
                                    <input
                                        type="date"
                                        value={editEventDate}
                                        onChange={e => setEditEventDate(e.target.value)}
                                    />
                                </div>
                                <div className="col">
                                    <label>Event Time</label>
                                    <input
                                        type="time"
                                        value={editEventTime}
                                        onChange={e => setEditEventTime(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Venue <span style={{ color: '#555', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <div className="venue-input-wrap">
                                    <span className="venue-icon">📍</span>
                                    <input
                                        type="text"
                                        placeholder="e.g. Grand Arena, Mumbai"
                                        value={editVenue}
                                        onChange={e => setEditVenue(e.target.value)}
                                        className="venue-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Description <span style={{ color: '#555', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <textarea
                                    rows={3}
                                    placeholder="Event description..."
                                    value={editDescription}
                                    onChange={e => setEditDescription(e.target.value)}
                                    className="admin-textarea"
                                />
                            </div>

                            <div className="edit-modal-actions">
                                <button type="button" className="edit-cancel-btn" onClick={() => setEditTicket(null)}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={editLoading} className="admin-btn" style={{ marginTop: 0 }}>
                                    {editLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Ticket Modal */}
            {isCreateModalOpen && (
                <div className="ticket-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                    <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="edit-modal-header">
                            <div>
                                <h3 className="edit-modal-title">Launch New Tickets</h3>
                            </div>
                            <button className="close-modal-btn-static" onClick={() => setIsCreateModalOpen(false)}>✕</button>
                        </div>

                        <form onSubmit={handleCreate} className="edit-modal-form">
                            <div className="form-group row">
                                <div className="col">
                                    <label>Ticket Type</label>
                                    <select value={ticketType} onChange={e => setTicketType(e.target.value)}>
                                        <option value="premium">Premium</option>
                                        <option value="basic">Basic</option>
                                        <option value="normal">Normal</option>
                                    </select>
                                </div>

                                <div className="col">
                                    <label>Event Type</label>
                                    <select value={eventType} onChange={e => setEventType(e.target.value)}>
                                        <option value="musical">Musical</option>
                                        <option value="techno">Techno</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col">
                                    <label>Count</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        required
                                        value={count}
                                        onChange={e => setCount(e.target.value === '' ? '' : Number(e.target.value))}
                                    />
                                </div>
                                <div className="col">
                                    <label>Amount (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        required
                                        value={amount}
                                        onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <div className="col">
                                    <label>Event Date</label>
                                    <input
                                        type="date"
                                        value={eventDate}
                                        onChange={e => setEventDate(e.target.value)}
                                    />
                                </div>
                                <div className="col">
                                    <label>Event Time</label>
                                    <input
                                        type="time"
                                        value={eventTime}
                                        onChange={e => setEventTime(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Venue <span style={{ color: '#555', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <div className="venue-input-wrap">
                                    <input
                                        type="text"
                                        placeholder="e.g. Grand Arena, Mumbai"
                                        value={venue}
                                        onChange={e => setVenue(e.target.value)}
                                        className="venue-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Description <span style={{ color: '#555', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <textarea
                                    rows={3}
                                    placeholder="Event description..."
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    className="admin-textarea"
                                />
                            </div>

                            <div className="edit-modal-actions">
                                <button type="button" className="edit-cancel-btn" onClick={() => setIsCreateModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={loading} className="admin-btn" style={{ marginTop: 0 }}>
                                    {loading ? 'Creating...' : 'Create Tickets'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
